import { zodResolver } from '@hookform/resolvers/zod'
import {
    Box
} from '@mui/material'
import { FormProvider, useForm } from 'react-hook-form'
import { useIntl } from 'react-intl'
import { useNotificationStack } from '../../app/hooks/useNotificationStack'
import { useGetTransportModeQuery } from '../../app/redux-fetch/apiCoreQuery'
import { useCreateCarrierMutation, useUpdateCarrierMutation } from '../../app/redux-fetch/apiOrders'
import SubmitButtons from '../../components/Buttons/SubmitButtons'
import FormInput from '../../components/Forms/FormInput'
import FormSelect from '../../components/Forms/FormSelect'
import SideModalWrapper from '../Assets/modals/SideModalWrapper'
import { messages } from './messages'
import { CarrierFormData, carrierSchema } from './schema'
import { ICarrier } from './types'

type CarrierFormProps = {
    carrier: ICarrier
    handleClose: () => void
    isOpen: boolean
}

const CarrierForm = ({ carrier, handleClose, isOpen }: CarrierFormProps) => {
    const { formatMessage } = useIntl()
    const isNew = !carrier?.id || carrier?.id === 0

    const [addCarrier] = useCreateCarrierMutation()
    const [updateCarrier] = useUpdateCarrierMutation()
    const { data: transportModes = [] } = useGetTransportModeQuery()
    const { enqueueSuccess, enqueueFailure } = useNotificationStack()

    const methods = useForm<CarrierFormData>({
        resolver: zodResolver(carrierSchema),
        defaultValues: carrier,
    })

    const {
        handleSubmit,
    } = methods

    const onSubmit = async (data: CarrierFormData) => {
        if (isNew) {
            await addCarrier({ tenantId: data.tenantId, body: data })
                .then(() => {
                    enqueueSuccess(messages.carrierCreated)
                    handleClose()
                })
                .catch(() => enqueueFailure(messages.carrierNotAdded))
        } else {
            await updateCarrier({ tenantId: data.tenantId, body: data })
                .then(() => {
                    enqueueSuccess(messages.carrierUpdated)
                    handleClose()
                })
                .catch(() => enqueueFailure(messages.carrierNotUpdated))
        }
    }

    const onSubmitHandler = handleSubmit(onSubmit)

    return (
        <SideModalWrapper
            open={isOpen}
            onClose={handleClose}
            headerLabel={formatMessage(messages.createEditCarrier)}
        >
            <FormProvider {...methods}>
                <form onSubmit={handleSubmit(onSubmit)}>
                    <Box sx={{ minWidth: '450px', m: '32px' }}>
                        <FormInput
                            name="name"
                            label={formatMessage(messages.name)}
                            required
                            maxLength={100}
                        />
                        <FormInput
                            name="code"
                            label={formatMessage(messages.code)}
                            maxLength={10}
                        />
                        <FormSelect
                            name="transportModeId"
                            label={formatMessage(messages.transportMode)}
                            options={transportModes.map((mode) => ({
                                value: mode.id,
                                label: mode.name
                            }))}
                            fullWidth
                        />
                    </Box>
                    <Box
                        sx={{
                            position: 'fixed',
                            bottom: 0,
                            width: '100%',
                            padding: '16px',
                            backgroundColor: '#fff',
                            borderTop: '1px solid #e0e0e0',
                            zIndex: 999,
                        }}
                    >
                        <SubmitButtons create={isNew} isEnabled={true} onCancel={handleClose} submitForm={onSubmitHandler} />
                    </Box>
                </form>
            </FormProvider>
        </SideModalWrapper>
    )
}

export default CarrierForm
